import React, { Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import { store } from './redux/store';
import { ApolloClient, ApolloProvider, InMemoryCache } from '@apollo/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { SnackbarProvider } from './components/snackbar';
import ProposalThemeProvider from './theme';
import ProposalSkeleton from './components/ProposalSkeleton';

/**
 * Each product line is split into its own chunk. These were previously static
 * imports, which is why a visitor opening one translation proposal downloaded
 * the interpretation and brochure apps as well — a 4.83 MB main bundle.
 */
const InterpretClientOutput = lazy(() => import('./pages/InterpretClientProposal'));
const BrochureClientOutput = lazy(() => import('./pages/Brochures'));
const TranslationClientOutput = lazy(() => import('./pages/TranslationProposal'));

// Print surface: same components, print stylesheet, no chrome. Headless
// Chromium loads this route to generate the PDF.
const PrintProposal = lazy(() => import('./print/PrintProposal'));

const client = new ApolloClient({
  uri: `${process.env.REACT_APP_BACKEND_URL}/graphql`,
  cache: new InMemoryCache({ addTypename: false })
});

const withSuspense = (element) => (
  <Suspense fallback={<ProposalSkeleton />}>{element}</Suspense>
);

const router = createBrowserRouter([
  { path: '/', element: <div>Hello world!</div> },
  {
    path: '/interpretation/:lang/:id',
    element: withSuspense(<InterpretClientOutput />)
  },
  {
    path: '/brochure/:lang/:id',
    element: withSuspense(<BrochureClientOutput />)
  },
  {
    path: '/translation/:lang/:id',
    element: withSuspense(<TranslationClientOutput />)
  },
  {
    path: '/print/:type/:lang/:id',
    element: withSuspense(<PrintProposal />)
  }
]);

createRoot(document.getElementById('root')).render(
  <ApolloProvider client={client}>
    <ProposalThemeProvider>
      <SnackbarProvider>
        <Provider store={store}>
          <RouterProvider router={router} />
        </Provider>
      </SnackbarProvider>
    </ProposalThemeProvider>
  </ApolloProvider>
);

reportWebVitals();
